import { useState, useEffect, useRef } from 'react';
import { useQuote } from '../context/QuoteContext';

/**
 * LivePriceModule - Displays delayed/last price for a stock symbol.
 * 
 * Now uses QuoteContext for batch fetching instead of individual API calls.
 * When wrapped in QuoteProvider, all instances share a single bulk request.
 * 
 * Shows:
 * - "DELAYED" + amber dot + "15m" badge when market is open
 * - "LAST" + red dot + "LAST" badge when market is closed
 */
const LivePriceModule = ({ symbol }) => {
  const { quote, loading, error } = useQuote(symbol);
  const [priceChanged, setPriceChanged] = useState(false);
  const prevPriceRef = useRef(null);

  // Animate on price change
  useEffect(() => {
    if (quote?.price && prevPriceRef.current !== null && prevPriceRef.current !== quote.price) {
      setPriceChanged(true);
      setTimeout(() => setPriceChanged(false), 450);
    }
    if (quote?.price) {
      prevPriceRef.current = quote.price;
    }
  }, [quote?.price]);

  // Get change color
  const getChangeColor = () => {
    if (!quote || quote.change === 0) return '#a1a1aa';
    return quote.change > 0 ? '#4ade80' : '#f87171';
  };

  // Determine if market is closed - only check marketStatus.isOpen, not source
  // When market is open but price is unavailable, still show as "delayed" (amber)
  const isMarketClosed = quote?.marketStatus?.isOpen === false;
  
  // Colors based on market status
  const indicatorColor = isMarketClosed ? '#ef4444' : '#fbbf24'; // Red when closed, amber when open
  const labelText = isMarketClosed ? 'Last' : 'Delayed';
  const badgeText = isMarketClosed ? 'LAST' : '15m';
  const subtitleText = isMarketClosed ? 'Last known price' : 'Price data is cached';

  return (
    <div 
      className="flex flex-col items-end"
      style={{ minWidth: '140px' }}
      data-testid="delayed-price-module"
    >
      {/* Price - Large */}
      <div 
        className={`transition-opacity duration-300 ${priceChanged ? 'opacity-60' : 'opacity-100'}`}
      >
        {loading && !quote ? (
          <div className="text-2xl font-bold" style={{ color: '#71717a' }}>-</div>
        ) : error && !quote ? (
          <div className="text-sm" style={{ color: '#71717a' }}>Unavailable</div>
        ) : !quote?.price || quote?.price === 0 ? (
          <div className="text-lg font-medium" style={{ color: '#71717a' }}>
            Price unavailable
          </div>
        ) : (
          <div className="text-2xl font-bold font-mono tracking-tight" style={{ color: '#FAFAFA' }}>
            {quote?.price?.toFixed(2)}{' '}
            <span className="text-sm font-normal" style={{ color: '#71717a' }}>
              {quote?.currency || 'USD'}
            </span>
          </div>
        )}
      </div>

      {/* Change - Medium */}
      {!loading && !error && quote && (
        <div 
          className={`text-sm font-medium font-mono mt-0.5 transition-opacity duration-300 ${priceChanged ? 'opacity-60' : 'opacity-100'}`}
          style={{ color: getChangeColor() }}
        >
          {quote.change >= 0 ? '+' : ''}{quote.change?.toFixed(2) || '0.00'}{' '}
          ({quote.changePercent >= 0 ? '+' : ''}{quote.changePercent?.toFixed(2) || '0.00'}%)
        </div>
      )}

      {/* Status indicator - changes based on market status */}
      <div className="flex items-center gap-1.5 mt-2">
        {/* Status dot - amber when open, red when closed */}
        <div 
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: indicatorColor }}
        />
        
        {/* Status label with badge */}
        <span 
          className="text-[10px] font-medium uppercase tracking-wider flex items-center gap-1"
          style={{ color: indicatorColor }}
        >
          {labelText}
          <span 
            className="text-[8px] px-1 py-0.5 rounded font-normal"
            style={{ backgroundColor: `${indicatorColor}26` }} // 26 = 15% opacity in hex
          >
            {badgeText}
          </span>
        </span>
      </div>

      {/* Cache info */}
      <div className="text-[10px] mt-0.5" style={{ color: '#52525b' }}>
        {subtitleText}
      </div>
    </div>
  );
};

export default LivePriceModule;
